import { useRef, useState, useEffect } from "react";
import { supabase, card, text, btn, btnSm, errorBanner, MUTED, SURFACE, LINE } from "./constants";
import { getCvSignedUrl, uploadCandidateCv } from "./lib/cv";
import { useIsMobile } from "./lib/responsive";

// Pestaña CV de la ficha. El bucket `cvs` es privado: el CV se enseña con un
// enlace firmado que se pide al abrir la pestaña (caduca en una hora; si
// alguien deja la pestaña abierta más tiempo, "Recargar" pide otro).
const CVTab = ({ candidate, isAdmin, lang }) => {
  const es = lang === "es";
  // Móvil: cabecera en dos filas, botones de 40 px a ancho completo y visor
  // más bajo (el PDF se abre mejor con Descargar). El escritorio no cambia.
  const isMobile = useIsMobile();
  const btnM = isMobile ? { flex: "1 1 0", minWidth: 0, height: 40, padding: "0 10px", justifyContent: "center", boxSizing: "border-box", whiteSpace: "nowrap", fontFamily: "inherit" } : null;
  const [hasCv, setHasCv] = useState(!!candidate.cvUrl);
  const [cvPath, setCvPath] = useState(candidate.cvUrl || "");
  const [signedUrl, setSignedUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cvUploading, setCvUploading] = useState(false);
  const [cvError, setCvError] = useState(null);
  const cvInputRef = useRef(null);

  const loadSigned = async () => {
    setLoading(true); setCvError(null);
    try {
      const url = await getCvSignedUrl(candidate.id);
      setSignedUrl(url);
      setHasCv(!!url);
    } catch (e) {
      console.error("CV link error:", e);
      setCvError((es ? "No se pudo abrir el CV: " : "Couldn't open the CV: ") + e.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    // Se pregunta siempre a la ruta, aunque la copia en memoria diga que no
    // hay CV: puede haberse subido después de cargar la lista.
    setSignedUrl(null); setHasCv(!!candidate.cvUrl); setCvPath(candidate.cvUrl || "");
    loadSigned();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidate.id]);

  const handleCvUpload = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.includes("pdf")) { setCvError(es ? "Solo se admiten archivos PDF." : "Only PDF files are accepted."); return; }
    setCvError(null);
    setCvUploading(true);
    try {
      const path = await uploadCandidateCv(candidate.id, file);
      const { data, error } = await supabase.from("candidates").update({ cv_url: path }).eq("id", candidate.id).select("id");
      // RLS bloqueado = 200 con 0 filas: el archivo subió pero la ficha no lo sabría
      if (error || !data || data.length === 0) throw new Error(error?.message || (es ? "el archivo subió pero no se pudo enlazar a la ficha" : "file uploaded but couldn't be linked to the profile"));
      setCvPath(path);
      await loadSigned();
    } catch (err) {
      console.error("CV upload error:", err);
      setCvError((es ? "Error al subir: " : "Upload error: ") + (err.message || JSON.stringify(err)));
    }
    setCvUploading(false);
  };

  // Los CV del intake pueden ser .doc/.docx: el navegador no los enseña en
  // el visor, solo se descargan.
  const isPdf = !/\.(docx?)(\?|#|$)/i.test(cvPath || "");

  return (
    <div style={{ ...card, padding: isMobile ? 16 : 22, width: "100%", boxSizing: "border-box" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: isMobile ? 14 : 20, ...(isMobile ? { flexDirection: "column", alignItems: "stretch", gap: 12 } : null) }}>
        <h3 style={{ ...text.cardTitle, margin: 0 }}>CV</h3>
        <div style={{ display: "flex", gap: isMobile ? 8 : 10, alignItems: "center" }}>
          {signedUrl && (
            <a href={signedUrl} target="_blank" rel="noopener noreferrer" download
              style={{ ...btn.ghost, ...btnSm, display: "flex", alignItems: "center", gap: 6, textDecoration: "none", ...btnM }}>
              ↓ {es ? "Descargar" : "Download"}
            </a>
          )}
          {hasCv && !loading && (
            <button onClick={loadSigned} title={es ? "Pide un enlace nuevo (caducan en una hora)" : "Get a fresh link (they expire after an hour)"}
              style={{ ...btn.ghost, ...btnSm, ...btnM }}>
              {es ? "Recargar" : "Reload"}
            </button>
          )}
          {isAdmin && (
            <>
              <input ref={cvInputRef} type="file" accept="application/pdf" style={{ display: "none" }} onChange={handleCvUpload} />
              <button onClick={() => { setCvError(null); cvInputRef.current?.click(); }} disabled={cvUploading}
                style={{ ...(cvUploading ? btn.disabled : btn.primary), ...btnSm, ...btnM }}>
                {cvUploading ? (es ? "Subiendo..." : "Uploading...") : hasCv ? (es ? "↻ Reemplazar CV" : "↻ Replace CV") : (es ? "↑ Subir CV" : "↑ Upload CV")}
              </button>
            </>
          )}
        </div>
      </div>
      {cvError && (
        <div style={{ ...errorBanner, marginBottom: 16 }}>
          {cvError}
        </div>
      )}
      {loading ? (
        <div style={{ background: SURFACE, border: `1.5px solid ${LINE}`, borderRadius: 14, padding: isMobile ? "40px 16px" : "60px 32px", textAlign: "center", ...text.body, color: MUTED }}>
          {es ? "Cargando CV..." : "Loading CV..."}
        </div>
      ) : signedUrl && isPdf ? (
        <div style={{ borderRadius: 10, overflow: "hidden", border: `1.5px solid ${LINE}` }}>
          <iframe src={signedUrl + "#toolbar=1&navpanes=0"} style={{ width: "100%", height: isMobile ? "70dvh" : 820, border: "none", display: "block" }} title="CV" />
        </div>
      ) : signedUrl ? (
        <div style={{ background: SURFACE, border: `1.5px solid ${LINE}`, borderRadius: 14, padding: isMobile ? "32px 16px" : "40px 32px", textAlign: "center", ...text.body, color: MUTED }}>
          {es ? "Este CV es un documento de Word: no se puede previsualizar. Usa Descargar." : "This CV is a Word document and can't be previewed. Use Download."}
        </div>
      ) : !hasCv ? (
        <div style={{ background: SURFACE, border: `1.5px solid ${LINE}`, borderRadius: 14, padding: isMobile ? "40px 16px" : "60px 32px", textAlign: "center" }}>
          <div style={{ ...text.body, color: MUTED, marginBottom: 8 }}>{es ? "No hay CV subido aún." : "No CV uploaded yet."}</div>
          {isAdmin
            ? <div style={text.empty}>{es ? "Haz clic en 'Subir CV' para añadir el PDF." : "Click 'Upload CV' to add the PDF file."}</div>
            : <div style={text.empty}>{es ? "El CV estará disponible cuando el administrador lo suba." : "The CV will be available once the administrator uploads it."}</div>
          }
        </div>
      ) : null}
    </div>
  );
};

export default CVTab;
